import React, { useState, useRef, useEffect } from "react";
import { Button, Card } from "react-bootstrap";
import LeftNav from "../common/leftnav";
import { useUser } from "../../context/UserContext";
import authService from "../../services/authService";

const TestNoura = ({ showLeftNav = true }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [ephemeralKey, setEphemeralKey] = useState(null);
  const { token, user } = useUser();
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const audioElRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [quotaExceeded, setQuotaExceeded] = useState(false);

  const activeStartTimeRef = useRef(null);

  useEffect(() => {
    if(user?.remaining_minutes < 0){
      setQuotaExceeded(true);
    } else {
      const fetchSessionDetails = async () => {
        try {
          const data = await authService.getOpenAiSession(token);
          const EPHEMERAL_KEY = data.client_secret.value;
          setEphemeralKey(EPHEMERAL_KEY);
        } catch (error) {
          console.error("Error fetching session details:", error);
        } finally {
          setIsLoading(false);
        }
      };

      fetchSessionDetails();
    }
  }, [token, user]);

  const handleMicClick = async () => {
    setIsPlaying((prevState) => !prevState);

    if (!isPlaying && !quotaExceeded) {
      try {
        if (!ephemeralKey) {
          return;
        }

        activeStartTimeRef.current = Date.now();
        const pc = new RTCPeerConnection();
        pcRef.current = pc;

        const audioEl = document.createElement("audio");
        audioEl.autoplay = true;
        audioElRef.current = audioEl;
        document.body.appendChild(audioEl);
        pc.ontrack = (e) => {
          if (e.streams && e.streams[0]) {
            audioEl.srcObject = e.streams[0];
          }
        };

        const micStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        localStreamRef.current = micStream;
        pc.addTrack(micStream.getTracks()[0]);

        const dc = pc.createDataChannel("oai-events");
        dc.onopen = () => {
          const conversationMessage = {
            type: "conversation.item.create",
            previous_item_id: null,
            item: {
              id: "msg_" + Date.now(),
              type: "message",
              role: "user",
              content: [
                {
                  type: "input_text",
                  text: "Hello.",
                },
              ],
            },
          };
          sendMessage(conversationMessage);
        };

        dc.addEventListener("message", async (e) => {
          const data = JSON.parse(e.data);

          if (
            data.type ===
            "conversation.item.input_audio_transcription.completed"
          ) {
            const userTranscript = data.transcript;
            if (userTranscript) {
              setConversation((prev) => [
                ...prev,
                { role: "user", content: userTranscript },
              ]);
  
              const data = {
                transcript: userTranscript 
              }
              // Query Pinecone for context
              const context = await authService.queryPineconeForContext(data, token);
              if (context) {
                const responseMessage = {
                  type: "response.create",
                  response: {
                    instructions: `Based on the following context:\n${context}\n\nRespond to the user's query: ${userTranscript}`,
                  },
                };
                sendMessage(responseMessage);
              }
              else {
                const responseMessage = {
                  type: "response.create",
                  response: {
                    instructions: `Respond to the user's query: ${userTranscript}`,
                  },
                };
                sendMessage(responseMessage);
              }
            }
          }

          if (data.type === "response.done" && data.response?.output) {
            const transcript = data.response.output
              .filter(
                (item) => item.type === "message" && item.role === "assistant"
              )
              .map(
                (item) =>
                  item.content.find((c) => c.type === "audio")?.transcript
              )
              .join(" ");

            if (transcript) {
              setConversation((prev) => [
                ...prev,
                { role: "assistant", content: transcript },
              ]);
            }
          }

          if (data.type === "response.output_item.done") {
            const sessionItem = data["item"];
            if (sessionItem.type === "function_call") {
              const data = {
                function_name: sessionItem["name"],
                arguments: sessionItem["arguments"],
                call_id: sessionItem["call_id"],
              };
              const handleFunctionCall = await authService.handleFunctionCall(
                data,
                token
              );

              if (dc?.readyState === "open") {
                sendMessage(handleFunctionCall);
              }
            }
          }
        });

        const sendMessage = async (message) => {
          if (dc?.readyState === "open") {
            dc.send(JSON.stringify(message));
            dc.send(JSON.stringify({ type: "response.create" }));
          }
        };

        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        const baseUrl = "https://api.openai.com/v1/realtime";
        // const model = "gpt-4o-realtime-preview-2024-12-17";
        const model = "gpt-4o-mini-realtime-preview-2024-12-17";
        const sdpResponse = await fetch(`${baseUrl}?model=${model}`, {
          method: "POST",
          body: offer.sdp,
          headers: {
            Authorization: `Bearer ${ephemeralKey}`,
            "Content-Type": "application/sdp",
          },
        });

        const answer = {
          type: "answer",
          sdp: await sdpResponse.text(),
        };
        await pc.setRemoteDescription(answer);
      } catch (error) {
        setIsPlaying(false);
      }
    } else {
      if (activeStartTimeRef.current) {
        const activeDuration = (Date.now() - activeStartTimeRef.current) / 1000;
        activeStartTimeRef.current = null;
        const mins = (activeDuration / 60).toFixed(2);
        const data = {
          usedMinutes: Number(mins),
        };
        await authService.postMinutes(token, data);
      }
      stopWebRTCConnection();
      setIsPlaying(false);
    }
  };

  const stopWebRTCConnection = () => {
    const pc = pcRef.current;
    const localStream = localStreamRef.current;
    const audioEl = audioElRef.current;

    if (pc) {
      pc.close();
      pcRef.current = null;
    }

    if (localStream) {
      localStream.getTracks().forEach((track) => track.stop());
      localStreamRef.current = null;
    }

    if (audioEl) {
      audioEl.remove();
      audioElRef.current = null;
    }
  };

  return (
    <div className="d-flex">
      {showLeftNav && <LeftNav />}
      <div className="dashboard-content flex-grow-1 p-4 bg-light">
        {showLeftNav && <h2 className="fw-bold fs-five-title">Test Nouraa</h2>}

        <Card className="p-4 mt-4 rounded-4 border-0">
          <Card.Body className="py-5">
            <div className="mx-auto  d-flex justify-content-center mb-3 mic-container gradient-border">
              <div
                className={`inner-circle ${
                  isPlaying
                    ? "pulse-animation"
                    : isLoading
                    ? "inner-circle-disabled"
                    : "inner-circle-play bg-light rounded-circle d-flex justify-content-center align-items-center"
                }`}
                onClick={!isLoading ? handleMicClick : undefined}
                style={{ cursor: isLoading ? "not-allowed" : "pointer" }}
              >
                <img
                  src={
                    isPlaying ? "/assets/stop_mic.svg" : "/assets/play_mic.svg"
                  }
                  alt="Mic"
                  style={{ opacity: isLoading ? 0.5 : 1 }}
                />
              </div>
            </div>
            <Card.Title className="fw-bold text-center fs-6">
              Click to speak with your agent
            </Card.Title>
            <Card.Text className="text-muted fs-one text-center">
              Test how your customer will experience Nouraa
            </Card.Text>
          </Card.Body>
          <Card className="mt-5 px-0 p-3 rounded border-light-subtle">
            <Card.Body>
              <Card.Title className="fw-bold fs-6">Conversations</Card.Title>
              <hr className="text-body-tertiary" />
              <Card.Text className=" pb-5">
                {conversation.map((msg, index) =>
                  msg.role === "assistant" ? (
                    <div key={index} className="mb-2">
                      <strong>Nouraa</strong> <br /> {msg.content}
                    </div>
                  ) : (
                    <div key={index} className="mb-2 text-end">
                      <strong>You</strong> <br /> {msg.content}
                    </div>
                  )
                )}
              </Card.Text>
            </Card.Body>
          </Card>
          <div className="mt-5">
            <h6 className="fw-bold my-4 fs-one">Try Asking:</h6>
            <div className="d-flex flex-wrap gap-4">
              <Button
                variant="outline-secondary"
                className="border-light-subtle p-3 fw-bold fs-one"
              >
                Tell me about your products
              </Button>
              <Button
                variant="outline-secondary"
                className="border-light-subtle p-3 fw-bold fs-one"
              >
                How do I contact support?
              </Button>
              <Button
                variant="outline-secondary"
                className="border-light-subtle p-3 fw-bold fs-one"
              >
                Help me with booking an appointment
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default TestNoura;
